import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchAssignedJobs } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge } from '../components/ui/Badge';

// "Assigned Job" — the first stage of the Field Lead workflow in
// docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md sec9.3. Shows every job where
// the authenticated Field Lead is Primary or an additional assigned Field
// Lead (server-scoped — see appAssignedJobIdSetForUser_ in src/Code.js and
// its v6 union with src/V6FieldOps.js's structured assignment table).
export function FieldPage() {
  const query = useQuery({
    queryKey: ['fieldAssignedJobs'],
    queryFn: fetchAssignedJobs,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Field Operations"
        title="Assigned Jobs"
        actions={
          <Link to="/field/roster" className="text-sm font-semibold text-gold-300 hover:text-gold-200">
            Worker Roster
          </Link>
        }
      />

      {query.isLoading && <LoadingState label="Loading assigned jobs…" />}

      {query.isError && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Could not load assigned jobs.'}
          onRetry={() => query.refetch()}
        />
      )}

      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load assigned jobs.'} onRetry={() => query.refetch()} />}

      {query.data?.ok && (query.data.jobs?.length ?? 0) === 0 && <EmptyState>No jobs are currently assigned to you.</EmptyState>}

      {query.data?.ok && (query.data.jobs?.length ?? 0) > 0 && (
        <ul className="space-y-2">
          {query.data.jobs!.map((job) => (
            <li key={job.jobId}>
              <Link
                to={`/field/${encodeURIComponent(job.jobId)}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-4 shadow-card transition hover:border-gold-700/60 hover:bg-ink-raised"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{job.customerName || job.jobId}</p>
                  <p className="truncate text-sm text-slate-400">{job.address || job.jobId}</p>
                </div>
                <Badge tone="gold" className="shrink-0">
                  {job.status || '—'}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
